import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { auth } from '../lib/api'
import { useMe } from '../lib/useMe'

const NAV_LINKS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/sites', label: 'Sites' },
  { to: '/keys', label: 'API-keys' },
]

// The header every authenticated page shares (app-shell-and-navigation plan §1a) —
// replaces each page's own one-off "← Terug naar ..." link. Renders nothing if there's
// no session yet/anymore: every page using this already redirects to /login on
// me.isError itself (see Onboarding.tsx's own guard and its regression test for why both
// the loading AND error checks matter — a shell showing a balance for a session that
// doesn't exist would be worse than showing nothing for one render).
export function AppShell() {
  const me = useMe()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [menuOpen, setMenuOpen] = useState(false)

  if (!me.data) return null

  async function onLogout() {
    await auth.logout()
    queryClient.setQueryData(['me'], undefined)
    navigate('/login')
  }

  return (
    <header className="border-b border-line">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
        <div className="flex items-center gap-8">
          <Link to="/dashboard" className="flex items-center">
            <img src="/app/logo.svg" alt="mentiontrend" className="h-8" />
          </Link>
          <nav className="hidden gap-6 sm:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={
                  location.pathname === link.to || location.pathname.startsWith(link.to + '/')
                    ? 'text-sm font-semibold text-ink'
                    : 'text-sm text-ink-soft hover:text-ink'
                }
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="hidden items-center gap-4 sm:flex">
          {/* The balance is deliberately just this header number plus a link, not a nav
              item and not a dashboard card: the dashboard is for whether the brand is
              being mentioned, and bookkeeping should not compete with that. */}
          <Link to="/credits" className="text-sm text-ink-soft hover:text-ink">
            {me.data.credits} credits
          </Link>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="text-sm text-ink-soft hover:text-ink"
            >
              {me.data.org_name} ▾
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-10 mt-2 w-56 rounded-lg border border-line bg-card py-1 shadow-lg">
                {!me.data.totp_enabled && (
                  <Link
                    to="/totp/setup"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-ink hover:bg-paper"
                  >
                    Tweestapsverificatie inschakelen
                  </Link>
                )}
                <button
                  type="button"
                  onClick={onLogout}
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-paper"
                >
                  Uitloggen
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          aria-label="Menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="text-ink sm:hidden"
        >
          ☰
        </button>
      </div>

      {menuOpen && (
        <nav className="flex flex-col gap-1 border-t border-line px-6 py-3 sm:hidden">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className="py-1.5 text-sm text-ink"
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-2 border-t border-line pt-2 text-sm text-ink-soft">
            {me.data.org_name} · {me.data.credits} credits
          </div>
          {!me.data.totp_enabled && (
            <Link to="/totp/setup" onClick={() => setMenuOpen(false)} className="py-1.5 text-sm text-ink">
              Tweestapsverificatie inschakelen
            </Link>
          )}
          <button type="button" onClick={onLogout} className="py-1.5 text-left text-sm text-ink">
            Uitloggen
          </button>
        </nav>
      )}
    </header>
  )
}
